import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

/* ─────────────────────────────────────────
   PAGE LOADER — affiché à chaque navigation
   ───────────────────────────────────────── */
const PageLoader = ({ visible }: { visible: boolean }) => (
  <div
    className="pointer-events-none fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white transition-opacity duration-300"
    style={{ opacity: visible ? 1 : 0 }}
    aria-hidden={!visible}
  >
    {/* Circuit decoration top-left */}
    <svg className="absolute left-0 top-0 h-40 w-40 opacity-10" viewBox="0 0 160 160" fill="none" aria-hidden="true">
      <polyline points="0,40 40,40 40,0" stroke="#1aa534" strokeWidth="2" />
      <polyline points="0,80 80,80 80,0" stroke="#1aa534" strokeWidth="1.5" />
      <circle cx="40" cy="40" r="5" fill="#1aa534" />
      <circle cx="80" cy="80" r="4" fill="#d2252e" />
      <polyline points="0,120 60,120 60,60 100,60" stroke="#d2252e" strokeWidth="1" />
    </svg>
    {/* Circuit decoration bottom-right */}
    <svg className="absolute bottom-0 right-0 h-40 w-40 opacity-10" viewBox="0 0 160 160" fill="none" aria-hidden="true">
      <polyline points="160,120 120,120 120,160" stroke="#1aa534" strokeWidth="2" />
      <polyline points="160,80 80,80 80,160" stroke="#1aa534" strokeWidth="1.5" />
      <circle cx="120" cy="120" r="5" fill="#1aa534" />
      <circle cx="80" cy="80" r="4" fill="#d2252e" />
      <polyline points="160,40 100,40 100,100 60,100" stroke="#d2252e" strokeWidth="1" />
    </svg>

    {/* Logo + spinner */}
    <div className="relative flex flex-col items-center gap-6">
      {/* Spinning ring */}
      <div className="relative h-24 w-24">
        <svg className="absolute inset-0 h-full w-full animate-spin" style={{ animationDuration: '1.4s' }}
          viewBox="0 0 96 96" fill="none" aria-hidden="true">
          <circle cx="48" cy="48" r="44" stroke="#e2e8f0" strokeWidth="4" />
          <path d="M48 4 A44 44 0 0 1 92 48" stroke="#1aa534" strokeWidth="4" strokeLinecap="round" />
        </svg>
        {/* Second arc */}
        <svg className="absolute inset-0 h-full w-full animate-spin" style={{ animationDuration: '2.1s', animationDirection: 'reverse' }}
          viewBox="0 0 96 96" fill="none" aria-hidden="true">
          <path d="M48 92 A44 44 0 0 1 4 48" stroke="#d2252e" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.6" />
        </svg>
        {/* Logo in center */}
        <img
          src="/logo.jpg"
          alt="BETRobionics loading"
          className="absolute inset-0 m-auto h-14 w-14 rounded-xl border border-slate-200 object-cover shadow-sm"
        />
      </div>

      {/* Brand name */}
      <div className="text-center">
        <p className="text-lg font-bold text-slate-900">
          <span className="text-[#d2252e]">BETR</span>obionics
        </p>
        {/* Animated dots */}
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {[0, 1, 2].map(i => (
            <span key={i}
              className="h-1.5 w-1.5 rounded-full bg-[#1aa534]"
              style={{ animation: `loaderDot 1.2s ease-in-out ${i * 0.2}s infinite` }}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ── Scroll to top on every route change ── */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
};

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Solutions', to: '/poles' },
  { label: 'About', to: '/branches' },
  { label: 'Team', to: '/team' },
  { label: 'Contact', to: '/contact' },
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  /* Show loader briefly on every route change */
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(t);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <ScrollToTop />
      <PageLoader visible={loading} />

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">

            {/* Logo + Brand */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/logo.jpg"
                alt="BETRobionics"
                className="h-10 w-10 rounded-lg object-cover border border-slate-200 shadow-sm group-hover:shadow-md transition-shadow"
              />
              <div className="leading-tight">
                <span className="block text-base font-bold text-slate-900">
                  <span className="text-[#d2252e]">BETR</span>obionics
                </span>
                <span className="block text-[10px] text-slate-400 font-medium tracking-wide">
                  Burundi Emergency Technology Group
                </span>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    pathname === item.to
                      ? 'text-[#1aa534] bg-[#1aa534]/8'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <a
                href="tel:+25767706285"
                className="hidden lg:block text-sm font-medium text-slate-600 hover:text-[#1aa534] transition-colors"
              >
                +257 67 70 62 85
              </a>
              <Link to="/contact" className="btn-primary hidden md:inline-flex">
                Get in Touch
              </Link>
              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileOpen ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    pathname === item.to
                      ? 'text-[#1aa534] bg-[#1aa534]/8'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="btn-primary mt-2 justify-center"
              >
                Get in Touch
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* ══ MAIN ══ */}
      <main>
        <Outlet />
      </main>

      {/* ══ FOOTER ══ */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-4">

            {/* Brand */}
            <div className="lg:col-span-1 space-y-4">
              <Link to="/" className="flex items-center gap-3 group">
                <img
                  src="/logo.jpg"
                  alt="BETRobionics"
                  className="h-12 w-12 rounded-xl object-cover border border-white/10"
                />
                <div>
                  <p className="text-lg font-bold text-white">
                    <span className="text-[#ff6b6b]">BETR</span>obionics
                  </p>
                  <p className="text-[11px] text-slate-500">Burundi Emergency Technology Group</p>
                </div>
              </Link>
              <p className="text-sm leading-6 text-slate-400">
                Designing smart IoT, AI and robotics solutions for Africa's future.
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="h-2 w-2 rounded-full bg-[#1aa534] animate-pulse" />
                <span className="text-slate-400">Bujumbura, Burundi</span>
              </div>
            </div>

            {/* Services links */}
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Services</p>
              <div className="space-y-2 text-sm">
                {['IoT Solutions', 'Electronics & Embedded', 'Robotics', 'Consulting & Integration', 'Training', 'E-commerce'].map((s) => (
                  <Link key={s} to="/services" className="block text-slate-400 hover:text-white transition-colors">
                    {s}
                  </Link>
                ))}
              </div>
            </div>

            {/* Company links */}
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Company</p>
              <div className="space-y-2 text-sm">
                {navItems.map((item) => (
                  <Link key={item.to} to={item.to} className="block text-slate-400 hover:text-white transition-colors">
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Contact</p>
              <div className="space-y-3 text-sm">
                <p>
                  <span className="block text-[11px] text-slate-500 uppercase tracking-wide mb-0.5">Email</span>
                  <a href="mailto:contact@betr.bi" className="text-slate-300 hover:text-white transition-colors">
                    contact@betr.bi
                  </a>
                </p>
                <p>
                  <span className="block text-[11px] text-slate-500 uppercase tracking-wide mb-0.5">Phone</span>
                  <a href="tel:+25767706285" className="text-slate-300 hover:text-white transition-colors">
                    +257 67 70 62 85
                  </a>
                </p>
                <p>
                  <span className="block text-[11px] text-slate-500 uppercase tracking-wide mb-0.5">Location</span>
                  <span className="text-slate-400">Bujumbura, Burundi</span>
                </p>
              </div>
              <Link to="/contact" className="btn-primary inline-flex text-xs px-4 py-2 mt-1">
                Contact Us →
              </Link>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 sm:flex-row">
            <div className="flex items-center gap-2">
              <img src="/logo.jpg" alt="BETR" className="h-6 w-6 rounded object-cover opacity-60" />
              <p className="text-xs text-slate-500">
                © {new Date().getFullYear()} BETRobionics · Burundi Emergency Technology Group. All rights reserved.
              </p>
            </div>
            <p className="text-xs text-slate-600">IoT · Robotics · AI · Electronics · Training</p>
          </div>

          {/* Made with love signature */}
          <div className="mt-4 flex items-center justify-center gap-1.5 border-t border-slate-800/60 pt-4">
            <span className="text-xs text-slate-600">Fait avec</span>
            <svg className="h-3.5 w-3.5 text-[#d2252e] fill-current" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span className="text-xs text-slate-600">par</span>
            <span className="text-xs font-semibold text-slate-300">Ir Masenge</span>
            <span className="text-xs text-slate-600">de</span>
            <a
              href="https://ygo.bi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-[#1aa534] hover:text-[#40c74e] transition-colors"
            >
              ygo.bi
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
